"use client"
import { Menu, X, ChevronDown } from "lucide-react"
import { useState } from "react"
import { Link } from "react-router-dom"
import { editions } from "../data/events"
import { vizag2026 } from "../data/vizag2026"
import { VizagLogo } from "./VizagLogo"

export function Header({ onBackToHome, currentEdition, setCurrentEdition, onContactClick, onPresentationsClick, onAgendaClick }) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isEventsDropdownOpen, setIsEventsDropdownOpen] = useState(false)
  const [isMobileEventsOpen, setIsMobileEventsOpen] = useState(false)
  const isVizag2026 = currentEdition === vizag2026.id

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const handleLinkClick = (event, sectionId) => {
    event.preventDefault()
    setIsMobileMenuOpen(false)
    const section = document.getElementById(sectionId)
    if (section) {
      section.scrollIntoView({ behavior: "smooth" })
    }
  }

  const handleContactClick = (event) => {
    event.preventDefault()
    setIsMobileMenuOpen(false)
    if (onContactClick) onContactClick()
  }

  const handleEditionChange = (edition) => {
    setCurrentEdition(edition)
    setIsEventsDropdownOpen(false)
    setIsMobileEventsOpen(false)
    setIsMobileMenuOpen(false)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  return (
    <>
      <header className="bg-white/80 backdrop-blur-md border-b border-green-100 sticky top-0 z-50">
        <div className={isVizag2026 ? "w-full px-4 py-3 sm:px-6" : "container mx-auto px-4 py-4"}>
          <div className="flex items-center justify-between">
            <Link to="/" aria-label="CTS homepage" className="flex shrink-0 items-center gap-4 rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-green-600" onClick={onBackToHome}>
              {isVizag2026 ? (
                <>
                  <VizagLogo className="w-20 shadow-sm sm:w-24 lg:w-28" />
                  <span className="hidden lg:block">
                    <span className="block text-xl font-bold tracking-tight text-green-800">CTS 2026</span>
                    <span className="mt-1 block text-xs font-semibold uppercase tracking-widest text-green-600">Vizag Edition</span>
                  </span>
                </>
              ) : <img src={currentEdition === "chennai" ? "/images/chennai/CTS_3D copy.png" : "/images/cts_logo.png"} alt="CTS Logo" className={currentEdition === "chennai" ? "h-14 w-auto" : "h-10 w-auto"} />}
              {/* <div className="text-green-700 text-sm font-medium">Powered by Candela Technologies</div> */}
            </Link>

            {/* Desktop Navigation */}
            <nav className={`hidden items-center gap-6 ${isVizag2026 ? "xl:flex" : "md:flex"}`}>
              {/* Events Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setIsEventsDropdownOpen(!isEventsDropdownOpen)}
                  aria-expanded={isEventsDropdownOpen}
                  aria-controls="desktop-events"
                  className="flex items-center gap-1 text-green-700 hover:text-green-800 font-medium transition-colors"
                >
                  Events
                  <ChevronDown className={`w-4 h-4 transition-transform ${isEventsDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {isEventsDropdownOpen && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setIsEventsDropdownOpen(false)}
                    ></div>
                    <div id="desktop-events" className="absolute top-full right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-green-100 py-2 z-50">
                      {editions.map((edition) => (
                        <button key={edition.id} onClick={() => handleEditionChange(edition.id)} aria-current={currentEdition === edition.id ? "page" : undefined} className={`w-full text-left px-4 py-2 hover:bg-green-50 transition-colors ${currentEdition === edition.id ? "bg-green-100 text-green-800 font-semibold" : "text-green-700"}`}>
                          {edition.name}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>

              {/* Event-specific navigation links */}
              {currentEdition === "vizag-2026" && (
                <>
                  <button type="button" onClick={onAgendaClick} aria-haspopup="dialog" className="font-medium text-green-700 hover:text-green-800">Agenda</button>
                  <Link to={`${vizag2026.route}#sessions`} onClick={onPresentationsClick ? (e) => { e.preventDefault(); onPresentationsClick() } : undefined} className="font-medium text-green-700 hover:text-green-800">Presentations</Link>
                  <Link to="/vizag-2026/badge" onClick={() => setIsMobileMenuOpen(false)} className="rounded-full bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700">Create your badge</Link>
                  <Link to={`${vizag2026.route}#previous-editions`} className="font-medium text-green-700 hover:text-green-800">Previous Editions</Link>
                </>
              )}
              {(currentEdition === "vizag" || currentEdition === "chennai") && (
                <>
                  <a
                    href="#speakers"
                    onClick={(e) => handleLinkClick(e, "speakers")}
                    className="text-green-700 hover:text-green-800 font-medium transition-colors"
                  >
                    Speakers
                  </a>
                  {currentEdition === "vizag" && (
                  <a
                    href="#sessions"
                    onClick={(e) => handleLinkClick(e, "sessions")}
                    className="text-green-700 hover:text-green-800 font-medium transition-colors"
                  >
                    Sessions
                  </a>
                  )}
                  <a
                    href="#panel-discussion"
                    onClick={(e) => handleLinkClick(e, "panel-discussion")}
                    className="text-green-700 hover:text-green-800 font-medium transition-colors"
                  >
                    Panel Discussion
                  </a>
                </>
              )}

              {/* Contact - Always visible */}
              <a
                href="#contact"
                onClick={handleContactClick}
                className="text-green-700 hover:text-green-800 font-medium transition-colors"
              >
                Contact
              </a>

              {currentEdition === "chennai" && (
                <Link
                  to="/im-attending"
                  className="rounded-full bg-green-600 px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-green-700"
                >
                  I Attended
                </Link>
              )}
            </nav>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className={`p-2 text-green-700 hover:text-green-800 transition-colors ${isVizag2026 ? "xl:hidden" : "md:hidden"}`}
              aria-label="Toggle mobile menu"
              aria-expanded={isMobileMenuOpen}
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className={`fixed inset-0 z-50 ${isVizag2026 ? "xl:hidden" : "md:hidden"}`}>
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={toggleMobileMenu}></div>
          <div className="absolute right-0 top-0 h-full w-64 overflow-y-auto bg-white shadow-xl">
            <div className="p-6">
              <div className="flex justify-end mb-8">
                <button
                  onClick={toggleMobileMenu}
                  className="p-2 text-green-700 hover:text-green-800 transition-colors"
                  aria-label="Close mobile menu"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Mobile Navigation Links */}
              <nav className="flex flex-col gap-6">
                {/* Events Dropdown - Mobile */}
                <div>
                  <button
                    onClick={() => setIsMobileEventsOpen(!isMobileEventsOpen)}
                    aria-expanded={isMobileEventsOpen}
                    aria-controls="mobile-events"
                    className="flex items-center justify-between w-full text-lg font-medium text-green-800 hover:text-green-600 cursor-pointer transition-colors"
                  >
                    Events
                    <ChevronDown className={`w-5 h-5 transition-transform ${isMobileEventsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {isMobileEventsOpen && (
                    <div id="mobile-events" className="mt-3 ml-4 flex flex-col gap-3">
                      {editions.map((edition) => (
                        <button key={edition.id} onClick={() => handleEditionChange(edition.id)} aria-current={currentEdition === edition.id ? "page" : undefined} className={`text-left py-2 px-3 rounded-lg transition-colors ${currentEdition === edition.id ? "bg-green-100 text-green-800 font-semibold" : "text-green-700 hover:bg-green-50"}`}>
                          {edition.name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Event-specific navigation links */}
                {currentEdition === "vizag-2026" && (
                  <>
                    <button type="button" onClick={(event) => { setIsMobileMenuOpen(false); onAgendaClick?.(event) }} aria-haspopup="dialog" className="text-left text-lg font-medium text-green-800 hover:text-green-600">Agenda</button>
                    <Link to={`${vizag2026.route}#sessions`} onClick={(e) => { setIsMobileMenuOpen(false); if (onPresentationsClick) { e.preventDefault(); onPresentationsClick() } }} className="text-lg font-medium text-green-800 hover:text-green-600">Presentations</Link>
                    <Link to="/vizag-2026/badge" onClick={() => setIsMobileMenuOpen(false)} className="rounded-full bg-green-600 px-4 py-2 font-semibold text-white hover:bg-green-700">Create your badge</Link>
                  <Link to={`${vizag2026.route}#previous-editions`} onClick={() => setIsMobileMenuOpen(false)} className="text-lg font-medium text-green-800 hover:text-green-600">Previous Editions</Link>
                  </>
                )}
                {(currentEdition === "vizag" || currentEdition === "chennai") && (
                  <>
                    <a
                      href="#speakers"
                      onClick={(e) => handleLinkClick(e, "speakers")}
                      className="text-lg font-medium text-green-800 hover:text-green-600 cursor-pointer transition-colors"
                    >
                      Speakers
                    </a>
                    {currentEdition === "vizag" && (
                    <a
                      href="#sessions"
                      onClick={(e) => handleLinkClick(e, "sessions")}
                      className="text-lg font-medium text-green-800 hover:text-green-600 cursor-pointer transition-colors"
                    >
                      Sessions
                    </a>
                    )}
                    <a
                      href="#panel-discussion"
                      onClick={(e) => handleLinkClick(e, "panel-discussion")}
                      className="text-lg font-medium text-green-800 hover:text-green-600 cursor-pointer transition-colors"
                    >
                      Panel Discussion
                    </a>
                  </>
                )}

                {/* Contact - Always visible */}
                <a
                  href="#contact"
                  onClick={handleContactClick}
                  className="text-lg font-medium text-green-800 hover:text-green-600 cursor-pointer transition-colors"
                >
                  Contact
                </a>

                {currentEdition === "chennai" && (
                  <Link
                    to="/im-attending"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="rounded-full bg-green-600 px-4 py-2 text-center text-lg font-semibold text-white transition-colors hover:bg-green-700"
                  >
                    I&apos;m Attending
                  </Link>
                )}
              </nav>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
